import * as React from "react"
import { cn } from "../../lib/utils"
import { buttonVariants } from "./button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

function Calendar({ className, selected, onSelect, month: propMonth, disabled, ...props }) {
  const [currentMonth, setCurrentMonth] = React.useState(() => {
    return propMonth || selected || new Date()
  })

  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear()
    const startYear = currentYear - 10
    const endYear = currentYear + 2
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i)
  }, [])

  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ]

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    
    // Получаем день недели первого дня (0 = воскресенье, нужно сделать 0 = понедельник)
    let firstDayOfWeek = firstDay.getDay()
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
    
    const days = []
    
    // Добавляем пустые ячейки для дней предыдущего месяца
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null)
    }
    
    // Добавляем дни текущего месяца
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const handleMonthChange = (monthIndex) => {
    const newDate = new Date(currentMonth)
    newDate.setMonth(parseInt(monthIndex))
    setCurrentMonth(newDate)
  }

  const handleYearChange = (year) => {
    const newDate = new Date(currentMonth)
    newDate.setFullYear(parseInt(year))
    setCurrentMonth(newDate)
  }

  const handleDayClick = (date) => {
    if (date && onSelect) {
      onSelect(date)
    }
  }

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear()
  }

  const isToday = (date) => {
    if (!date) return false
    const today = new Date()
    return isSameDay(date, today)
  }

  const isDisabled = (date) => {
    if (!date || !disabled) return false
    if (typeof disabled === 'function') {
      return disabled(date)
    }
    return false
  }

  const days = getDaysInMonth(currentMonth)

  return (
    <div className={cn("p-3", className)} {...props}>
      {/* Заголовок с селектами */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <Select
          value={currentMonth.getMonth().toString()}
          onValueChange={handleMonthChange}
        >
          <SelectTrigger className="h-7 w-[110px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((monthName, index) => (
              <SelectItem key={index} value={index.toString()}>
                {monthName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={currentMonth.getFullYear().toString()}
          onValueChange={handleYearChange}
        >
          <SelectTrigger className="h-7 w-[80px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Дни недели */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-muted-foreground h-8 flex items-center justify-center"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Дни месяца */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          const isSelectedDay = selected && date && isSameDay(date, selected)
          const isTodayDay = date && isToday(date)
          const isDisabledDay = date && isDisabled(date)

          return (
            <button
              key={index}
              onClick={() => handleDayClick(date)}
              disabled={!date || isDisabledDay}
              className={cn(
                buttonVariants({ variant: "ghost" }),
                "h-8 w-8 p-0 font-normal",
                !date && "invisible",
                isSelectedDay && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                isTodayDay && !isSelectedDay && "bg-accent text-accent-foreground",
                isDisabledDay && "text-muted-foreground opacity-50 cursor-not-allowed"
              )}
            >
              {date ? date.getDate() : ''}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export { Calendar }
